import React from 'react';
import { DevicePhoneMobileIcon, ComputerDesktopIcon, DeviceTabletIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

interface Section {
  id: string;
  type: string;
  title?: string;
  subtitle?: string;
  description?: string;
  content?: string;
  image?: string;
  items?: any[];
  features?: string[];
}

interface LivePreviewProps {
  pageTitle: string;
  pageSlug: string;
  sections: Section[];
  onClose: () => void;
}

type DeviceType = 'desktop' | 'tablet' | 'mobile';

const LivePreview: React.FC<LivePreviewProps> = ({ pageTitle, pageSlug, sections, onClose }) => {
  const [device, setDevice] = React.useState<DeviceType>('desktop');

  const deviceWidths: Record<DeviceType, string> = {
    desktop: '100%',
    tablet: '768px',
    mobile: '375px',
  };

  const renderSection = (section: Section, index: number) => {
    switch (section.type) {
      case 'hero':
        return (
          <div key={index} className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16 px-8">
            <div className="max-w-4xl mx-auto text-center">
              {section.subtitle && (
                <span className="text-blue-200 text-sm font-medium uppercase tracking-wider">{section.subtitle}</span>
              )}
              <h1 className="text-4xl md:text-5xl font-bold mt-2 mb-4">{section.title}</h1>
              {section.description && (
                <p className="text-xl text-blue-100 max-w-2xl mx-auto" dangerouslySetInnerHTML={{ __html: section.description }} />
              )}
              {section.image && (
                <img src={section.image} alt={section.title} className="mt-8 mx-auto max-w-md rounded-lg shadow-xl" />
              )}
            </div>
          </div>
        );

      case 'content':
        return (
          <div key={index} className="py-12 px-8 bg-white">
            <div className="max-w-4xl mx-auto">
              {section.title && <h2 className="text-3xl font-bold text-gray-900 mb-4">{section.title}</h2>}
              {section.description && (
                <div className="text-gray-600 mb-4" dangerouslySetInnerHTML={{ __html: section.description }} />
              )}
              {section.content && (
                <div className="prose prose-lg max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: section.content }} />
              )}
            </div>
          </div>
        );

      case 'features-grid':
        return (
          <div key={index} className="py-12 px-8 bg-gray-50">
            <div className="max-w-6xl mx-auto">
              {section.subtitle && (
                <span className="text-blue-600 text-sm font-medium uppercase tracking-wider">{section.subtitle}</span>
              )}
              {section.title && <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-4">{section.title}</h2>}
              {section.description && (
                <p className="text-gray-600 mb-8 max-w-2xl" dangerouslySetInnerHTML={{ __html: section.description }} />
              )}
              {section.items && section.items.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {section.items.map((item, i) => (
                    <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                        <span className="text-blue-600 text-xl">✓</span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'services-grid':
        return (
          <div key={index} className="py-12 px-8 bg-white">
            <div className="max-w-6xl mx-auto">
              {section.title && <h2 className="text-3xl font-bold text-gray-900 mb-4">{section.title}</h2>}
              {section.description && (
                <p className="text-gray-600 mb-8 max-w-2xl" dangerouslySetInnerHTML={{ __html: section.description }} />
              )}
              {section.items && section.items.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {section.items.map((item, i) => (
                    <div key={i} className="group">
                      {item.image && (
                        <img src={item.image} alt={item.title} className="w-full h-48 object-cover rounded-xl mb-4" />
                      )}
                      <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'alternating':
        return (
          <div key={index} className="py-12 px-8 bg-white">
            <div className="max-w-6xl mx-auto">
              <div className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}>
                <div className="flex-1">
                  {section.title && <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>}
                  {section.description && (
                    <div className="text-gray-600 mb-4" dangerouslySetInnerHTML={{ __html: section.description }} />
                  )}
                  {section.content && (
                    <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: section.content }} />
                  )}
                  {section.features && section.features.length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {section.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-700">
                          <span className="text-green-500">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {section.image && (
                  <div className="flex-1">
                    <img src={section.image} alt={section.title} className="rounded-xl shadow-lg" />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'testimonials':
        return (
          <div key={index} className="py-12 px-8 bg-gray-50">
            <div className="max-w-4xl mx-auto text-center">
              {section.subtitle && (
                <span className="text-blue-600 text-sm font-medium uppercase tracking-wider">{section.subtitle}</span>
              )}
              {section.title && <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-4">{section.title}</h2>}
              {section.description && (
                <p className="text-gray-600" dangerouslySetInnerHTML={{ __html: section.description }} />
              )}
              <div className="mt-8 bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <p className="text-gray-600 italic">"Testimonials will be loaded from the database..."</p>
              </div>
            </div>
          </div>
        );

      case 'contact-info':
        return (
          <div key={index} className="py-12 px-8 bg-blue-600 text-white">
            <div className="max-w-4xl mx-auto">
              {section.title && <h2 className="text-3xl font-bold mb-4">{section.title}</h2>}
              {section.description && (
                <p className="text-blue-100 mb-6" dangerouslySetInnerHTML={{ __html: section.description }} />
              )}
              {section.content && (
                <div className="text-blue-100" dangerouslySetInnerHTML={{ __html: section.content }} />
              )}
            </div>
          </div>
        );

      default:
        return (
          <div key={index} className="py-8 px-8 bg-gray-100 border-2 border-dashed border-gray-300">
            <div className="max-w-4xl mx-auto text-center text-gray-500">
              <p className="font-medium">Section: {section.type}</p>
              {section.title && <p className="text-sm mt-1">{section.title}</p>}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-4">
            <h3 className="font-bold text-gray-900">Live Preview</h3>
            <span className="text-sm text-gray-500 font-mono">/{pageSlug}</span>
          </div>
          
          {/* Device Switcher */}
          <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-gray-200">
            <button
              onClick={() => setDevice('desktop')}
              className={`p-2 rounded-md transition-colors ${device === 'desktop' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="Desktop"
            >
              <ComputerDesktopIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setDevice('tablet')}
              className={`p-2 rounded-md transition-colors ${device === 'tablet' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="Tablet"
            >
              <DeviceTabletIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setDevice('mobile')}
              className={`p-2 rounded-md transition-colors ${device === 'mobile' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="Mobile"
            >
              <DevicePhoneMobileIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <a
              href={`https://tak8.com.au/${pageSlug === 'home' ? '' : pageSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
              View Live
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto bg-gray-200 p-4">
          <div
            className="mx-auto bg-white shadow-lg transition-all duration-300 overflow-hidden"
            style={{
              width: deviceWidths[device],
              maxWidth: '100%',
              borderRadius: device === 'mobile' ? '24px' : device === 'tablet' ? '16px' : '8px',
            }}
          >
            {/* Browser Chrome */}
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 font-mono truncate">
                  tak8.com.au/{pageSlug === 'home' ? '' : pageSlug}
                </div>
              </div>
            </div>

            {/* Page Content */}
            <div className="min-h-[400px]">
              {sections && sections.length > 0 ? (
                sections.map((section, index) => renderSection(section, index))
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-400">
                  <div className="text-center">
                    <p className="text-lg font-medium">No sections yet</p>
                    <p className="text-sm">Add sections to see the preview</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivePreview;
